import React, { useMemo, useState } from "react";

import deleteReviewVote from "@/api/requests/deleteReviewVote";
import postFlagReview from "@/api/requests/postFlagReview";
import postReviewVote from "@/api/requests/postReviewVote";
import { RetailType } from "@/custom-types/Dispensary";
import { DispensaryAddress } from "@/custom-types/DispensaryAddress";
import {
  DispensaryReview,
  ProductReview,
  StrainReview,
} from "@/custom-types/Reviews";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getIsLoggedIn } from "@/redux/selectors/user";
import { getRetailerInfo } from "@/utils/getRetailerInfo";
import { retailCopyFormatter } from "@/utils/retailCopyFormatter";

import ReviewCard from "@/components/botanic/ReviewCard";

export enum ReviewType {
  DISPENSARY = "dispensary",
  PRODUCT = "product",
  STRAIN = "strain",
}

const ReviewContainer: React.FC<{
  hideMetadata?: boolean;
  retailType?: string;
  review: DispensaryReview | StrainReview | ProductReview;
  type?: ReviewType;
  itemReviewed?: string;
  dispensaryAddress?: DispensaryAddress;
}> = ({
  hideMetadata,
  retailType,
  itemReviewed,
  review,
  type = ReviewType.DISPENSARY,
  dispensaryAddress,
}) => {
  const { publishEvent } = useEventTracker();
  const countryCode = useDomainCountryCode();

  const isLoggedIn = useSelector(getIsLoggedIn);

  const {
    hasCurrentUserFlagged,
    hasCurrentUserUpvoted,
    responseDate,
    responseText,
  } = review as DispensaryReview | ProductReview;
  const [hasReported, setHasReported] = useState(hasCurrentUserFlagged);
  const [hasUpvoted, setHasUpvoted] = useState(hasCurrentUserUpvoted);

  const { upvotesCount } = review as DispensaryReview | StrainReview;
  const { upvoteCount: productUpvoteCount } = review as ProductReview;
  const [upvoteCount, setUpvoteCount] = useState(
    upvotesCount || productUpvoteCount,
  );

  const addVote = async () => {
    const postReviewSuccess = await postReviewVote(review.id);

    if (postReviewSuccess) {
      setUpvoteCount(upvoteCount + 1);
      setHasUpvoted(true);
      publishEvent({
        action: "ThumbsUp",
        category: "Dispensary Reviews",
        label: "Add",
      });
    }
  };

  const deleteVote = async () => {
    const deleteReviewVoteSuccess = await deleteReviewVote(review.id);

    if (deleteReviewVoteSuccess) {
      setUpvoteCount(upvoteCount - 1);
      setHasUpvoted(false);
      publishEvent({
        action: "ThumbsUp",
        category: "Dispensary Reviews",
        label: "Remove",
      });
    }
  };

  const toggleReviewVote = async () => {
    if (isLoggedIn) {
      if (hasUpvoted) {
        deleteVote();
      } else {
        addVote();
      }
    }
  };

  const flagReview = () => {
    postFlagReview(review.id, type).then((flagReviewSuccess) => {
      if (flagReviewSuccess) {
        setHasReported(true);
        // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133) events with no label?
        publishEvent({
          action: "ReportAbuse",
          category: "Dispensary Reviews",
        });
      }
    });
  };

  const handleReportAction = () => {
    if (isLoggedIn && !hasReported) {
      flagReview();
    }
  };

  const responseName = useMemo(() => {
    switch (type) {
      case ReviewType.DISPENSARY:
        return retailCopyFormatter(
          getRetailerInfo(countryCode, retailType as RetailType).copy,
          "capitalize",
        );
      case ReviewType.PRODUCT:
        return "Brand";
      default:
        return "";
    }
  }, [type, countryCode, retailType, review]);

  return (
    <>
      <div className="inline-block mb-lg w-full">
        <ReviewCard
          badges={(review as DispensaryReview).badges}
          created={review.created}
          hasReported={hasReported}
          hasUserVoted={hasUpvoted}
          hideMetadata={hideMetadata}
          itemReviewed={itemReviewed}
          rating={review.rating}
          referringPageType={type}
          reportAction={handleReportAction}
          reportedFeelings={(review as StrainReview).reportedFeelings}
          responseDate={responseDate}
          responseName={responseName}
          responseText={responseText}
          text={review.text}
          truncate={true}
          upvoteCount={upvoteCount}
          username={review.user.username}
          userPublicId={review.user.publicId}
          voteAction={toggleReviewVote}
          dispensaryAddress={dispensaryAddress}
        />
      </div>
    </>
  );
};

export default ReviewContainer;
